/**
 * Pass 4 of the roster tick: who lets a bomb go, where the ones already falling
 * have got to, and which of them go off.
 *
 * Split out of `encounter-tick.ts` because it is the one pass that walks *both*
 * halves of the roster — a Gotha and a Zeppelin differ in every respect except
 * the ones that decide where a bomb lands, so both feed the same solver through
 * the `Raider` shape below. `encounter-tick.ts` keeps the ordering contract and
 * the passes that only touch aeroplanes.
 *
 * Release is decided off the **pre-step** pose for the same reason a round's
 * muzzle is: it is the picture the crew was looking at when the lever moved. A
 * bomber that has been shot to pieces does not bomb — `raidersOf` drops it
 * before the solver ever sees it. Nothing here draws from the RNG.
 *
 * **Two ways a bomb leaves a rack, one ballistic solver.** An AI rack is aimed:
 * it predicts the fall every tick and lets go when the answer is inside
 * `bombAimToleranceM`. A *player-flown* rack is commanded: the toggle
 * (`FlightInputs.bombRelease`) is the whole decision, because WWI bombing from a
 * scout was eyeball work and a solver that dropped for him would be flying the
 * aeroplane. Both then go through `releaseBomb` and fall under the same gravity,
 * and both are spaced by the same `bombReleaseIntervalSeconds`, so a held toggle
 * walks a stick at a bomber's own cadence instead of emptying the rack on one
 * tick. The player carrying anything at all is a port extension, opt-in per
 * mission — see docs/game/player-bombing.md.
 */

import type { Tunables } from "../flight/tunables.ts";
import type { FlightInputs } from "../flight/inputs.ts";
import { FLAT_GROUND, type GroundQuery } from "../flight/ground.ts";
import type { Vec3 } from "../flight/math3d.ts";
import {
  advanceBombs,
  aimErrorM,
  bombWind,
  rackReady,
  releaseBomb,
  shouldRelease,
  type BombAir,
  type BombImpact,
  type BombRecord,
  type ReleaseView,
} from "./bombs.ts";
import type { CombatAircraft } from "./encounter.ts";
import { bodyOf, type CombatBody } from "./geometry.ts";
import { targetBody, targetProfileOf, type EncounterTarget } from "./targets.ts";

/** What the bomb pass did: the bombs still falling, the ones that went off, and who let go. */
export interface BombRun {
  readonly bombs: readonly BombRecord[];
  readonly impacts: readonly BombImpact[];
  /** Ids that released a bomb this tick — aeroplanes and Zeppelins alike. */
  readonly released: ReadonlySet<string>;
}

export interface BombPassInput {
  readonly targets: readonly EncounterTarget[];
  /** Bombs still falling from earlier ticks. */
  readonly bombs: readonly BombRecord[];
  readonly tick: number;
  readonly dt: number;
  readonly tunables: Tunables;
  /**
   * This tick's stick for a player-flown aeroplane, by id. Only ever read for
   * the one with no AI pilot, and only when it is carrying something.
   */
  readonly playerInputs?: (id: string) => FlightInputs;
  /** What is under the aeroplane, for a hand-released bomb's impact height. Flat datum when the caller has no terrain. */
  readonly ground?: GroundQuery;
  /**
   * The air the whole raid — racks and falling bombs alike — is working in
   * (AD-007k §9). Omitted by a caller with no weather to declare, which is still
   * air.
   */
  readonly air?: BombAir;
}

/** Anything with a rack, flown or not. */
interface Raider {
  readonly id: string;
  readonly side: CombatAircraft["side"];
  readonly body: CombatBody;
  readonly bombsRemaining: number;
  readonly bombsReleased: number;
  readonly bombTargetIds: readonly string[];
  readonly lastReleaseTick: number | null;
  /**
   * `null` for every rack the sim aims itself — bombers and Zeppelins. For a
   * player-flown one it is the toggle's state this tick, and the aiming solver
   * is not consulted at all: the pilot decides, the way a 1917 pilot leaning
   * over the side did.
   */
  readonly commanded: boolean | null;
}

/** The point on the ground a structure stands on — where its bombs are meant to go off. */
function aimPointOf(target: EncounterTarget): Vec3 {
  return {
    x: target.position.x,
    y: target.position.y - targetProfileOf(target.kind).centreHeightM,
    z: target.position.z,
  };
}

/** Every rack still in the fight, aeroplanes first and in roster order. */
function raidersOf(before: readonly CombatAircraft[], input: BombPassInput): Raider[] {
  const raiders: Raider[] = [];
  for (const entry of before) {
    if (entry.bombsRemaining <= 0 || entry.status !== "flying") continue;
    const commanded =
      entry.ai === null ? (input.playerInputs?.(entry.id).bombRelease ?? false) : null;
    raiders.push({ ...rackOf(entry), id: entry.id, side: entry.side, body: bodyOf(entry.sim), commanded });
  }
  for (const entry of input.targets) {
    if (entry.bombsRemaining <= 0 || entry.destroyed || entry.burning) continue;
    raiders.push({
      ...rackOf(entry),
      id: entry.id,
      side: entry.side,
      body: airRelativeBody(targetBody(entry), input.air),
      commanded: null,
    });
  }
  return raiders;
}

/**
 * A Zeppelin's velocity, as the air sees it.
 *
 * An aeroplane's `sim.velocity` is air-relative — wind never gets written back
 * into it (AD-007k §3) — so a bomb that inherits it, and is then carried by the
 * same wind, ends up on the aeroplane's *ground track*, which is the whole point
 * of §9's crab. A `targets.ts` body has no aerodynamics and no such distinction:
 * its `driftSpeedMps` is a **track over the ground**, an airship holding its
 * course for the city the way this port's routed aeroplanes now hold theirs. So
 * its rack has to hand the bomb the air-relative velocity that produces that
 * track, which is the track minus the wind — and the wind then puts it back.
 *
 * The net effect is that an airship's bombing is exactly as accurate in a gale
 * as in still air, which is correct for a body that navigates, and is what keeps
 * `intercept-zeppelin` a mission the airship can win. Without it the census
 * collapsed from 2/24 to 0/24 — the airship solved for a fall it was not going
 * to make and never released at all.
 */
function airRelativeBody(body: CombatBody, air: BombAir | undefined): CombatBody {
  const wind = bombWind(air, body.position.y);
  if (wind.x === 0 && wind.y === 0 && wind.z === 0) return body;
  return {
    ...body,
    velocity: {
      x: body.velocity.x - wind.x,
      y: body.velocity.y - wind.y,
      z: body.velocity.z - wind.z,
    },
  };
}

type Rack = Pick<Raider, "bombsRemaining" | "bombsReleased" | "bombTargetIds" | "lastReleaseTick">;

function rackOf(entry: Rack): Rack {
  return {
    bombsRemaining: entry.bombsRemaining,
    bombsReleased: entry.bombsReleased,
    bombTargetIds: entry.bombTargetIds,
    lastReleaseTick: entry.lastReleaseTick,
  };
}

/** One entry of an aiming list, if it is still there to be bombed by this side. */
function bombableTarget(
  targets: readonly EncounterTarget[],
  id: string,
  side: CombatAircraft["side"],
): EncounterTarget | null {
  const target = targets.find((candidate) => candidate.id === id);
  if (!target || target.destroyed || target.burning || target.side === side) return null;
  return target;
}

/**
 * Whether this bomber still has a job over its target — the question
 * `advanceRoute` asks before it lets a bomb-run leg complete (AD-007k §10).
 *
 * Three ways the answer is no, and they are the three ways a bomb run ends: the
 * rack is empty, the bomber is no longer flying, or **nothing on its aiming
 * list is still standing**. That last one is what keeps the hold honest. A raid
 * whose targets are already alight has been driven off in the only sense the
 * simulation has, and its bombers turn for home instead of circling a burning
 * yard with a live rack they cannot aim.
 *
 * It reads exactly what `bombAimOf` reads, through the same predicate, so the
 * leg is held for precisely as long as the solver would have something to solve
 * for.
 */
export function bombRunPending(
  raider: Pick<CombatAircraft, "side" | "status" | "bombsRemaining" | "bombTargetIds">,
  targets: readonly EncounterTarget[],
): boolean {
  if (raider.status !== "flying" || raider.bombsRemaining <= 0) return false;
  return raider.bombTargetIds.some((id) => bombableTarget(targets, id, raider.side) !== null);
}

/**
 * What this raider is aiming at: the one still standing on its list that its
 * *current* solution is closest to.
 *
 * Closest-solution rather than first-on-the-list because a bomb-aimer aims at
 * what is coming under the nose, and because two of the three things carrying a
 * rack cannot steer to fix a lateral error — a Zeppelin holds one course all the
 * way in, and a bomber holds its run. The list order still decides the raid's
 * *plan*: each bomber is routed over its own target, so its own target is the
 * one it can solve for.
 */
function bombAimOf(
  targets: readonly EncounterTarget[],
  raider: Raider,
  tunables: Tunables,
  air: BombAir | undefined,
): EncounterTarget | null {
  let best: EncounterTarget | null = null;
  let bestError = Number.POSITIVE_INFINITY;
  for (const id of raider.bombTargetIds) {
    const target = bombableTarget(targets, id, raider.side);
    if (!target) continue;
    // Range-only: the tick and interval are the *release* gate, which this is not.
    const error = aimErrorM({ ...raider, aimPoint: aimPointOf(target), tick: 0, dt: 0, tunables, air });
    if (error < bestError) {
      best = target;
      bestError = error;
    }
  }
  return best;
}

function viewOf(raider: Raider, aimPoint: Vec3, input: BombPassInput): ReleaseView {
  return { ...raider, aimPoint, tick: input.tick, dt: input.dt, tunables: input.tunables, air: input.air };
}

/** A bomber's or a Zeppelin's: it solves the fall and lets go when the answer is good. */
function aimedRelease(raider: Raider, input: BombPassInput): BombRecord | null {
  const aim = bombAimOf(input.targets, raider, input.tunables, input.air);
  if (!aim) return null;
  const view = viewOf(raider, aimPointOf(aim), input);
  if (!shouldRelease(view)) return null;
  return releaseBomb(view, raider.id, raider.bombsReleased);
}

/**
 * The player's: the toggle is the whole decision, and the bomb goes off where
 * the ground is *under the release point*.
 *
 * That last part is the one approximation. The impact height is sampled where
 * the aeroplane is rather than where the bomb will land a few hundred metres
 * ahead, exactly as an aimed release fixes its impact height at its aiming
 * point — on this port's flat world the two are the same number, and on sloping
 * terrain both are a height, not a hillside. It keeps `bombPositionAt` a closed
 * form and the record hashable, which is what a lockstep replay of a bomb needs.
 */
function commandedRelease(raider: Raider, input: BombPassInput): BombRecord | null {
  if (!raider.commanded) return null;
  const { x, z } = raider.body.position;
  const ground = input.ground ?? FLAT_GROUND;
  const view = viewOf(raider, { x, y: ground.groundHeightAt(x, z), z }, input);
  if (!rackReady(view)) return null;
  return releaseBomb(view, raider.id, raider.bombsReleased);
}

export function bombPass(before: readonly CombatAircraft[], input: BombPassInput): BombRun {
  const spawned: BombRecord[] = [];
  const released = new Set<string>();
  for (const raider of raidersOf(before, input)) {
    const record = raider.commanded === null ? aimedRelease(raider, input) : commandedRelease(raider, input);
    if (!record) continue;
    spawned.push(record);
    released.add(raider.id);
  }
  const swept = advanceBombs([...input.bombs, ...spawned], input.tick, input.dt, input.air);
  return { bombs: swept.bombs, impacts: swept.impacts, released };
}

/** The rack after this tick: one fewer bomb, one more gone, and the clock the stick's spacing runs on. */
export function rackAfter<T extends Omit<Rack, "bombTargetIds">>(
  entry: T,
  fired: boolean,
  tick: number,
): Omit<Rack, "bombTargetIds"> {
  if (!fired) {
    return {
      bombsRemaining: entry.bombsRemaining,
      bombsReleased: entry.bombsReleased,
      lastReleaseTick: entry.lastReleaseTick,
    };
  }
  return {
    bombsRemaining: Math.max(0, entry.bombsRemaining - 1),
    bombsReleased: entry.bombsReleased + 1,
    lastReleaseTick: tick,
  };
}
